import { useEffect, useState } from 'react';
import { Search, Filter } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

interface Student {
  id: string;
  name: string;
  email: string;
  paymentStatus: 'PAID' | 'UNPAID' | 'PENDING';
  enrolledDate?: string;
  enrolledCourses: {
    course: {
      id: string;
      name: string;
      title: string;
    };
  }[];
}

export function StudentsSection() {
  const [students, setStudents] = useState<Student[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [loading, setLoading] = useState(true);

  /**
   * FETCH STUDENTS WITH ENROLLMENTS FROM BACKEND
   */
  const fetchStudents = async () => {
    try {
      setLoading(true);

      // ✅ Get all students (admin only)
      const studentsRes = await fetch('/api/admin/students', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const studentsData = await studentsRes.json();

      // ✅ Get enrollments for each student
      const studentsWithEnrollments = await Promise.all(
        (studentsData.data || []).map(async (student: any) => {
          try {
            const enrollmentsRes = await fetch(`/api/admin/students/${student.id}/enrollments`, {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
              },
            });
            const enrollmentsData = await enrollmentsRes.json();
            
            return {
              ...student,
              enrolledCourses: enrollmentsData.data || [],
              paymentStatus: student.paymentStatus || 'PENDING',
            };
          } catch (err) {
            console.error(`Failed to fetch enrollments for student ${student.id}:`, err);
            return {
              ...student,
              enrolledCourses: [],
              paymentStatus: 'PENDING',
            };
          }
        })
      );

      setStudents(studentsWithEnrollments);
    } catch (err) {
      console.error('Failed to fetch students:', err);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Alternative: Single API call that returns students with enrollments
  const fetchStudentsOptimized = async () => {
    try {
      setLoading(true);
      
      const res = await fetch('/api/admin/students-with-enrollments', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const json = await res.json();
      
      setStudents(json.data || []);
    } catch (err) {
      console.error('Failed to fetch students:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStudentsOptimized(); // Use optimized version
    // fetchStudents(); // Or use the original version
  }, []);

  const filteredStudents = students.filter((student) => {
    const matchesSearch =
      student.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.email?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesFilter =
      filterStatus === 'all' || student.paymentStatus === filterStatus;

    return matchesSearch && matchesFilter;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PAID':
        return 'bg-green-100 text-green-700';
      case 'UNPAID':
        return 'bg-red-100 text-red-700';
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const handleExport = async () => {
    try {
      const res = await fetch('/api/admin/students/export', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const json = await res.json();
      console.log('Export data:', json.data);
      
      // Download as CSV
      if (json.data) {
        const csv = convertToCSV(json.data);
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'students-export.csv';
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const convertToCSV = (data: any[]) => {
    const headers = ['Name', 'Email', 'Status', 'Courses', 'Enrolled Date'];
    const rows = data.map(student => [
      student.name,
      student.email,
      student.paymentStatus,
      student.enrolledCourses?.map((ec: any) => ec.course.name).join('; ') || 'None',
      student.enrolledDate || 'N/A',
    ]);
    return [headers, ...rows].map(row => row.join(',')).join('\n');
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Students</h2>
          <p className="text-gray-500 mt-1">Manage enrolled students</p>
        </div>
        <Button onClick={handleExport} variant="outline">
          Export Data
        </Button>
      </div>

      {/* STATS */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="text-lg font-semibold">
          Total Students: <span className="text-blue-600">{students.length}</span>
        </div>
      </div>

      {/* FILTERS */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
        <div className="flex gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-48">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="PAID">Paid</SelectItem>
              <SelectItem value="PENDING">Pending</SelectItem>
              <SelectItem value="UNPAID">Unpaid</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* TABLE */}
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Courses</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-8">
                    Loading students...
                  </TableCell>
                </TableRow>
              ) : filteredStudents.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-8 text-gray-500">
                    No students found
                  </TableCell>
                </TableRow>
              ) : (
                filteredStudents.map((student) => (
                  <TableRow key={student.id}>
                    <TableCell className="font-medium">
                      {student.name || 'No name'}
                    </TableCell>

                    <TableCell>{student.email}</TableCell>

                    <TableCell>
                      <div className="flex flex-col gap-1">
                        {student.enrolledCourses && student.enrolledCourses.length > 0 ? (
                          student.enrolledCourses.map((ec) => (
                            <span key={ec.course.id} className="text-sm text-gray-600">
                              • {ec.course.name || ec.course.title}
                            </span>
                          ))
                        ) : (
                          <span className="text-sm text-gray-400">No courses enrolled</span>
                        )}
                      </div>
                    </TableCell>

                    <TableCell>
                      <Badge className={getStatusColor(student.paymentStatus)}>
                        {student.paymentStatus}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}